import {afterEveryRender, AfterRenderRef, EventEmitter, Injectable, OnDestroy, Output} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {Location} from '@angular/common';
import {filter, Subscription, SubscriptionLike} from 'rxjs';


export interface PageState {
  anchorId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PageStateService implements OnDestroy {

    private readonly navigationStartSubscription: Subscription;
    private readonly locationSubscription: SubscriptionLike;
    private readonly afterRenderRef: AfterRenderRef;
    private focusAnchorId?: string | null;

    private readonly anchorPrefixes = ['sm:', 'md:', 'lg:', 'xl:', '2xl:'];

    public pageState: PageState = {};

    @Output() beforeReplaceState = new EventEmitter<PageState>();
    @Output() elementFocused = new EventEmitter<HTMLElement>();

    constructor(private readonly router: Router,
                private readonly location: Location) {

      this.navigationStartSubscription =  this.router.events.pipe(
        filter(routerEvent => (routerEvent instanceof NavigationStart) &&
            (routerEvent.navigationTrigger === 'imperative') && (routerEvent.url !== this.router.url)))
          .subscribe(_navEvent => {

              this.focusAnchorId = null;

              const activeElement = document.activeElement;
              if (activeElement?.id) {
                this.pageState.anchorId = activeElement.id;
              }

              this.beforeReplaceState.emit(this.pageState);
              this.location.replaceState(router.url, '', this.pageState);

              this.pageState = {};
          });

      this.locationSubscription = this.location.subscribe(popState => {
        this.pageState = popState.state as PageState;
        this.focusAnchorId = this.pageState?.anchorId;
      });

      this.afterRenderRef = afterEveryRender(() => {

        if (this.focusAnchorId) {
          const elem = this.searchAnchorElement(this.focusAnchorId);
          if (elem) {
            // Der Focus soll erst ausgeführt werden, wenn andere Event-Handler ihre Arbeit getan haben (z.B. Einklappen von Overlays).
            setTimeout(() => {
                elem.focus();
                this.elementFocused.emit(elem);
            });
            this.focusAnchorId = null;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.navigationStartSubscription.unsubscribe();
    this.locationSubscription.unsubscribe();
    this.afterRenderRef?.destroy();
  }

  private isHidden(element: HTMLElement): boolean {
    if (element.hidden) {
      return true;
    }

    // element.offsetParent ist schwer testbar, deshalb folgende Alternativ-Implementierung
    const clientRect = element.getBoundingClientRect();
    return (clientRect.height === 0);
  }

  private searchAnchorElement(anchorId: string): HTMLElement | null {
    let element = document.getElementById(anchorId);
    if (element) {
        if (this.isHidden(element)) {
            element = this.searchAlternativeAnchorElement(this.normalizeAnchorId(anchorId));
        }
    } else {
        // Z.B. beim virtuellen Scrolling kann es sein, dass nur das Element mit dem Alternativ-Anker vorhanden ist,
        // da für den Hidden-Viewport das DOM nicht komplett aufgebaut wird.
        element = this.searchAlternativeAnchorElement(this.normalizeAnchorId(anchorId));
    }

    return element;
  }

  private normalizeAnchorId(anchorId: string): string {
    for (const prefix of this.anchorPrefixes) {
        if (anchorId.startsWith(prefix)) {
            return anchorId.substring(prefix.length);
        }
    }

    return anchorId;
  }

  private searchAlternativeAnchorElement(anchorId: string): HTMLElement | null {
    for (const prefix of ['', ...this.anchorPrefixes]) {
        const element = document.getElementById(prefix + anchorId);
        if (element && !this.isHidden(element)) {
            return element;
        }
    }

    return null;
  }

}
